import { LogEventInGA4 } from './../../utils/LogEventsInGoogleAnalytics';
import { Dispatch } from "../Dispatch";
import { ValidateServicesForSatss } from "./ConditionValidation";
import { CustomErrorMessages } from "../Utils/ErrorMessages";
import { VehicleOption } from "./Redux/ConditionEntities";
import { ApplicationState } from "../../appState";
import appstore from "../../appStore";
import { ServiceKind } from "../../utils/service-vehicles/ServiceMetadata";
import { ZONES_EXCLUDING_STANDARD_SILVER_SERVICE } from "../CuratedLocations/CuratedLocationZones";

/** After selecting a service, update various entities in the store and validate it against the location and payment method to display the error message. */
export function ProcessAndValidateSelectedService(selectedService: VehicleOption) {
    Dispatch.Condition.SelectVehicle(selectedService);

    // Log a GA4 event when a Vehicle is selected
    LogEventInGA4.SelectVehicle(selectedService.Service.displayName);

    Dispatch.UILogicControl.ValidateVehicleOnPickupChange(true);
    ValidateServicesForSatss();
}

/** 
 *  Derive the error message to be displayed along with the service selector.
 *  This method is suitable to be called from MapStateToProps()
 */
export function DeriveServiceErrorMessage(state: ApplicationState): string | null {
    const bookingFormUIState = state.uiLogicControl.BookingForm;

    if (!bookingFormUIState.IsSelectedVehicleServiceable) return CustomErrorMessages.ServiceNotAvailable;
    if (bookingFormUIState.IsInvalidConditionForSatss) return CustomErrorMessages.ServiceNotAvailableForSatss;

    return null;
}

/**
 * Handle automatic service selection adjustments for restricted curated location zones.
 * For zones where only Silver Service Exec vehicles are allowed (e.g., airports), 
 * this function automatically switches to Silver Service Exec if a non-exec service is currently selected.
 * 
 * @param zoneId - The curated location zone ID to check
 * @param isPickupLocation - Whether this is the pickup location (rules only apply to pickup)
 */
export function HandleRestrictedZoneServiceSelection(zoneId: number, isPickupLocation: boolean): void {
    // Only process for pickup locations in excluded zones
    if (!isPickupLocation || !ZONES_EXCLUDING_STANDARD_SILVER_SERVICE.includes(zoneId)) {
        return;
    }

    const currentSelectedService = appstore.getState().condition.SelectedCondition;
    
    // If already an Exec service, no need to switch
    if (currentSelectedService.Service.kind === ServiceKind.SilverServiceExec || 
        currentSelectedService.Service.kind === ServiceKind.SilverServiceExecXl) {
        return;
    }

    // Switch to Silver Service Exec for any non-exec service
    const conditionList = appstore.getState().condition.ConditionList;
    const silverExecOption = conditionList.find(
        vehicle => vehicle.Service.kind === ServiceKind.SilverServiceExec
    );
    
    if (silverExecOption) {
        ProcessAndValidateSelectedService(silverExecOption);
    }
}

/**
 * Filter vehicle options based on curated location zone restrictions.
 * For restricted zones (e.g., airports), only Silver Service Exec and Exec XL are allowed.
 * This whitelist approach ensures only approved vehicles pass through, even if unexpected services are returned.
 * 
 * @param vehicleOptions - The list of vehicle options to filter
 * @param state - The application state containing curated location information
 * @returns The filtered list of vehicle options
 */
export function FilterServicesForRestrictedZones(
    vehicleOptions: VehicleOption[], 
    state: ApplicationState
): VehicleOption[] {
    const pickupLocation = state.booking.Locations[0]; // Pickup is always at index 0
    const pickupZoneId = pickupLocation?.CuratedLocationZoneId;
    
    // If no zone ID or not a restricted zone, return all vehicles
    if (!pickupZoneId || !ZONES_EXCLUDING_STANDARD_SILVER_SERVICE.includes(pickupZoneId)) {
        return vehicleOptions;
    }

    // Whitelist: Only allow Silver Service Exec and Exec XL in restricted zones
    return vehicleOptions.filter(vehicle => 
        vehicle.Service.kind === ServiceKind.SilverServiceExec || 
        vehicle.Service.kind === ServiceKind.SilverServiceExecXl
    );
}
